// Curated list of common tokens shown in the "Request Money" picker.
// Rationale: when requesting a payment, we don't know which tokens the
// recipient holds, so we surface a small set of widely-held tokens per
// chain instead of the requester's own balances.

export interface CommonTokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  balance: string;
  logo?: string;
}

const NATIVE = '0x0000000000000000000000000000000000000000';
const isTestnet = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET';

const native = (symbol: string, name: string, decimals = 18): CommonTokenInfo => ({
  address: NATIVE,
  symbol,
  name,
  decimals,
  balance: '',
});

const erc20 = (
  address: string,
  symbol: string,
  name: string,
  decimals = 6,
): CommonTokenInfo => ({
  address,
  symbol,
  name,
  decimals,
  balance: '',
});

// Mainnet stablecoin addresses sourced from each issuer's docs. Testnets use
// native-only to avoid broken requests against wrong deployments.
const MAINNET_TOKENS: Record<string, CommonTokenInfo[]> = {
  ETHEREUM: [
    native('ETH', 'Ether'),
    erc20('0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', 'USDC', 'USD Coin'),
    erc20('0xdAC17F958D2ee523a2206206994597C13D831ec7', 'USDT', 'Tether USD'),
  ],
  BASE: [
    native('ETH', 'Ether'),
    erc20('0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', 'USDC', 'USD Coin'),
  ],
  ARBITRUM: [
    native('ETH', 'Ether'),
    erc20('0xaf88d065e77c8cC2239327C5EDb3A432268e5831', 'USDC', 'USD Coin'),
    erc20('0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', 'USDT', 'Tether USD'),
  ],
  POLYGON: [
    native('MATIC', 'Polygon', 18),
    erc20('0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', 'USDC', 'USD Coin'),
    erc20('0xc2132D05D31c914a87C6611C10748AEb04B58e8F', 'USDT', 'Tether USD'),
  ],
  AVALANCHE: [
    native('AVAX', 'Avalanche', 18),
    erc20('0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', 'USDC', 'USD Coin'),
    erc20('0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', 'USDT', 'Tether USD'),
  ],
  BSC: [
    native('BNB', 'BNB', 18),
    erc20('0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', 'USDC', 'USD Coin', 18),
    erc20('0x55d398326f99059fF775485246999027B3197955', 'USDT', 'Tether USD', 18),
  ],
};

// Fallback native-only lists for chains without curated stables (and for all
// testnets). The modal still lets the user pick the native token to request.
const NATIVE_ONLY: Record<string, CommonTokenInfo[]> = {
  ETHEREUM: [native('ETH', 'Ether')],
  BASE: [native('ETH', 'Ether')],
  ARBITRUM: [native('ETH', 'Ether')],
  POLYGON: [native('MATIC', 'Polygon', 18)],
  AVALANCHE: [native('AVAX', 'Avalanche', 18)],
  BSC: [native('BNB', 'BNB', 18)],
  SONEIUM: [native('SONY', 'Soneium', 18)],
  MATCHAIN: [native('BNB', 'BNB', 18)],
  ICP: [native('ICP', 'Internet Computer', 8)],
  AETERNITY: [native('AE', 'Aeternity', 18)],
  CASPER: [native('CSPR', 'Casper', 9)],
  TELOS: [native('TLOS', 'Telos', 18)],
  FLARE: [native('FLR', 'Flare', 18)],
  CHILIZ: [native('CHZ', 'Chiliz', 18)],
  BITFINITY: [native('BFT', 'Bitfinity', 18)],
  PWR_ETH_PLUS: [native('ETH', 'Ether')],
  PWR_BTC_PLUS: [native('BTC', 'Bitcoin', 8)],
  BITLAYER: [native('BTC', 'Bitcoin', 18)],
  BITCOIN: [native('BTC', 'Bitcoin', 8)],
};

export function getCommonTokens(chainKey: string): CommonTokenInfo[] {
  if (!isTestnet && MAINNET_TOKENS[chainKey]) return MAINNET_TOKENS[chainKey];
  return NATIVE_ONLY[chainKey] ?? [];
}
